// Portfolio Administration v1.0 PR 3 §6 — 7-pill assignment widget for
// Loan Administration. Mirrors the deal flow's widget but with the
// Loan-specific role taxonomy: B1 / B3 / B4 / E1-VM / E2 / E1 / B5.
//
// Pill state mapping (spec §6.2):
//   Unassigned     → outlined, muted text.
//   Assigned       → filled gold, onyx text.
//   NotApplicable  → strikethrough, muted red, no fill.

import { useMemo } from "react";
import { AssignmentPillPopover } from "./AssignmentPillPopover";
import {
  LOAN_ROLE_CODES,
  LOAN_ROLE_LABEL,
  type LoanAssignmentDto,
  type LoanRoleCode,
} from "@/api/portfolioAdmin/types";
import { useLoanAssignments } from "@/hooks/portfolioAdmin/usePortfolioAdmin";

interface SevenPillAssignmentWidgetProps {
  loanId: string;
}

function pillsByRole(
  assignments: LoanAssignmentDto[] | undefined,
): Record<LoanRoleCode, LoanAssignmentDto | undefined> {
  const out: Partial<Record<LoanRoleCode, LoanAssignmentDto>> = {};
  for (const a of assignments ?? []) {
    out[a.roleCode as LoanRoleCode] = a;
  }
  return out as Record<LoanRoleCode, LoanAssignmentDto | undefined>;
}

export function SevenPillAssignmentWidget({
  loanId,
}: SevenPillAssignmentWidgetProps) {
  const { data: assignments, isLoading } = useLoanAssignments(loanId);
  const byRole = useMemo(() => pillsByRole(assignments), [assignments]);

  if (isLoading) {
    return (
      <div
        className="flex items-center gap-space-1"
        data-testid="loan-assignment-pills-skeleton"
      >
        {LOAN_ROLE_CODES.map((r) => (
          <span
            key={r}
            className="inline-flex h-7 w-12 items-center justify-center rounded-pill border border-border-light bg-white-warm animate-pulse"
            aria-hidden
          />
        ))}
      </div>
    );
  }

  return (
    <div
      className="flex items-center gap-space-1"
      role="group"
      aria-label="Loan assignments"
      data-testid="loan-assignment-pills"
    >
      {LOAN_ROLE_CODES.map((role) => (
        <Pill
          key={role}
          loanId={loanId}
          role={role}
          assignment={byRole[role]}
        />
      ))}
    </div>
  );
}

interface PillProps {
  loanId: string;
  role: LoanRoleCode;
  assignment: LoanAssignmentDto | undefined;
}

function Pill({ loanId, role, assignment }: PillProps) {
  const status = assignment?.status ?? "Unassigned";
  const stateClass = stateClassName(status);
  const baseClass = `inline-flex items-center justify-center font-display font-bold uppercase tracking-label rounded-pill border transition-colors h-7 px-space-2 text-caption ${stateClass}`;

  const titleParts: string[] = [`${role} — ${LOAN_ROLE_LABEL[role]}`];
  if (status === "Assigned") titleParts.push("Assigned");
  else if (status === "NotApplicable") titleParts.push("Marked Not Applicable for this loan");
  else titleParts.push("Unassigned · click to assign");

  return (
    <AssignmentPillPopover
      loanId={loanId}
      role={role}
      assignment={assignment}
      trigger={
        <button
          type="button"
          className={`${baseClass} hover:ring-2 hover:ring-gold/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold`}
          title={titleParts.join(" · ")}
          aria-label={titleParts.join(" · ")}
          data-testid={`loan-pill-${role}`}
          data-status={status}
        >
          {role}
        </button>
      }
    />
  );
}

function stateClassName(status: string): string {
  switch (status) {
    case "Assigned":
      return "bg-gold text-onyx border-gold";
    case "NotApplicable":
      return "border-status-error/40 text-status-error line-through";
    case "Unassigned":
    default:
      return "border-border-light text-gray-mid bg-white-pure";
  }
}
